import React, { FC, useCallback, useRef, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { colorStack } from '../../../styleHelpers/colors';
import { media } from '../../../styleHelpers/breakpoint';
import { ILevel1MenuItem } from '../../../entities/IMenu';
import { WorkspaceLogo } from '../../Common/WorkspaceLogo/WorkspaceLogo';
import { clearPdfOverlay } from '../../..//actions/pdfOverlayActions';
import { camelize } from '../../../tools/string';
import { ToolTip } from '../../Common/ToolTip/ToolTip';
import { IState } from '../../../reducers';
import { IContextReducer } from '../../../reducers/contextReducer';

const APPROVALS_ROUTE = '/approvals';

const Wrapper = styled(Link) <{ $isHidden: boolean }>`
    cursor: pointer;
    display: flex;
    position: relative;
    justify-content: flex-start;
    align-items: center;
    text-decoration: none;
    min-height: 52px;
    padding: .5rem 1.1rem;
    color: ${colorStack.content};
    transition: all 0.3s;
    &:hover {
        color: ${colorStack.middleBlue};
        text-decoration: none;
    }
    ${({ $isHidden }) => $isHidden && css`
        visibility: hidden;
    `}
    &.active, &:active {
        background: ${colorStack.lightBlue};
        color: ${colorStack.darkBlue};
    }
    &.first-item {
        &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 10%;
            width: 80%;
            height: 2px;
            border-radius: 20px;
            background: ${colorStack.ligthGrey};
        }
    }
    &.last-item {
        margin-bottom: 1rem;
        &:after {
            content: '';
            position: absolute;
            bottom: calc(-0.5rem - 1px);
            left: 16px;
            width: 270px;
            max-width: calc(100% - 32px);
            height: 2px;
            border-radius: 20px;
            background: ${colorStack.ligthGrey};
        }
    }
`;

const Icon = styled(FontAwesomeIcon)`
    min-width: 32px;
`;

const ContextInfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const ContextName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    margin-left: .5rem;
    max-width: 15rem;
    text-overflow: ellipsis;
`;

const TooltipMenuWrapper = styled.div`
    margin: 0px;
    position: relative;
`;

const Badge = styled.div<{ $isCollapsedMenu?: boolean }>`
    position: absolute;
    right: 0.75rem;
    top: 25%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${colorStack.red};
    border: 1.5px solid ${colorStack.white};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    color: ${colorStack.white};
    z-index: 10;
    line-height: 1;
    pointer-events: none;
`;

const CollapsedMenuBadge = styled(Badge)`
    ${media.desktop`
        [data-sidebar-wrapper]:hover [data-sidebar-nav] & {
            display: none !important;
        }
    `}
`;

interface IMenuItem extends ILevel1MenuItem {
    isHidden?: boolean;
    isDesktopMenuVisible?: boolean;
    onClick?(orgUrlName: string);
}

export const MenuItem: FC<React.PropsWithChildren<IMenuItem>> = ({ imagePath, faIcon, svgIcon, name, link, orgUrlName, isHidden, fullMenu, onClick, className, isDesktopMenuVisible }) => {
    const dispatch = useDispatch();
    const { hasActiveApprovalRequests } = useSelector<IState, IContextReducer>(state => state.context);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [coords, setCoords] = useState<number[]>([]);

    const onElementClick = useCallback(() => {
        dispatch(clearPdfOverlay());
        onClick?.(orgUrlName);
    }, [orgUrlName, onClick]);

    const menuItemRef = useRef<HTMLDivElement>();
    const isLongText = useMemo(() => {
        return menuItemRef?.current?.scrollWidth > menuItemRef?.current?.offsetWidth;
    }, [menuItemRef?.current]);

    const showTooltipHandler = (e: React.MouseEvent<HTMLDivElement>) => {
        setCoords([e.clientX - (menuItemRef?.current?.offsetWidth / 2), menuItemRef?.current?.getBoundingClientRect().top + 30]);
        setShowTooltip(true);
    };

    const defaultLink = () => {
        return `${window.location.pathname}${window.location.search}${window.location.hash}`;
    };

    const isApprovalsMenuItem = link === APPROVALS_ROUTE;
    const showBadge = isApprovalsMenuItem && !isHidden && hasActiveApprovalRequests && (fullMenu || (!fullMenu && !isDesktopMenuVisible));

    return (
        <Wrapper onClick={onElementClick} to={link || defaultLink()} $isHidden={isHidden} className={`lc-st-sidebar-${name ? camelize(name) : ''} ${className?.length > 0 ? className : ''}`}>
            {showBadge && (fullMenu ? <Badge /> : <CollapsedMenuBadge />)}
            {svgIcon ? (
                <>{svgIcon}</>
            ) : faIcon ? (
                <Icon icon={faIcon} size="lg" />
            ) : (
                <WorkspaceLogo
                    picture={imagePath}
                    name={name}
                    id={name}
                    size={{ width: 32, height: 32 }}
                />
            )}
            <ContextInfoWrapper>
                {(fullMenu && name) && (
                    <TooltipMenuWrapper>
                        <ContextName
                            ref={menuItemRef}
                            onMouseEnter={showTooltipHandler}
                            onMouseLeave={() => setShowTooltip(false)}
                            onMouseMove={showTooltipHandler}
                        >
                            {name}
                        </ContextName>
                    </TooltipMenuWrapper>
                )}
            </ContextInfoWrapper>
            {(isLongText && showTooltip) && <ToolTip title={name} left={coords[0]} top={coords[1]} />}
        </Wrapper>
    );
};
